import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import T from 'prop-types';
import { forwardRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { warnOnce } from '../../helpers/warnOnce';
import * as colors from '../../styles/colors';
import { getFocusRing, resetButtonStyleWithTransition } from '../../styles/mixins';
import { rem } from '../../styles/rem';
import { buttonLabel } from '../../styles/typography';
import { unit } from '../../styles/unit';
import { Icon } from '../Icon/Icon';
import { Loader } from '../Loader/Loader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const slideDownTransitionStyle = /*#__PURE__*/css(["transition:opacity 75ms ease-out,transform 100ms ease-out;opacity:1;transform:translateY(0);[aria-busy] > &{opacity:0;transform:translateY(10px);}"]);
const ButtonLabel = /*#__PURE__*/styled.span.withConfig({
  displayName: "ButtonLabel",
  componentId: "sc-1a3rtp-0"
})(["", " display:inline-flex;height:100%;transition-delay:20ms;[aria-busy] > &{transition-delay:0ms;}"], slideDownTransitionStyle);
const ButtonIcon = /*#__PURE__*/styled(Icon).withConfig({
  displayName: "ButtonIcon",
  componentId: "sc-1a3rtp-1"
})(["", " transition-delay:0ms;[aria-busy] > &{transition-delay:20ms;}"], slideDownTransitionStyle);
const slideDownIn = /*#__PURE__*/keyframes(["0%{opacity:0;transform:translateY(-10px);}75%{opacity:1;}100%{transform:translateY(0);}"]);
const ButtonLoader = /*#__PURE__*/styled(Loader).withConfig({
  displayName: "ButtonLoader",
  componentId: "sc-1a3rtp-2"
})(["position:absolute;top:0;right:0;bottom:0;left:0;justify-content:center;align-items:center;animation:", " 100ms 100ms ease-out both;"], slideDownIn);
const intentColorMap = {
  default: 'black',
  primary: 'purple',
  danger: 'red',
  success: 'green',
  warning: 'yellow'
};

function getButtonVariant(props) {
  const shouldHaveDisabledStyle = (props.disabled || props['aria-disabled']) && !props['aria-busy'];

  switch (props.variant) {
    case 'secondary':
      {
        const {
          selected,
          intent
        } = props;
        const color = intentColorMap[intent];
        const outline = intent === 'default' ? 'purple' : color;
        const textColor = intent === 'primary' ? 'black' : color;
        const borderColor = outline;
        const selectedColor = outline;

        if (shouldHaveDisabledStyle) {
          if (selected) {
            return css(["color:", ";background-color:", ";border-color:", ";"], colors[`${selectedColor}32`], colors[`${selectedColor}8`], colors[`${selectedColor}32`]);
          }

          return css(["color:", ";border-color:", ";"], colors.black48, colors.black8);
        }

        return css(["color:", ";border-color:", ";&:hover{background-color:", ";}&:active{background-color:", ";}", " &.focus-visible{box-shadow:", ";}"], colors[textColor], colors[`${borderColor}Alpha56`], colors.blackAlpha4, colors.blackAlpha8, p => p.selected && css(["background-color:", ";border-color:", ";color:", ";&:hover,&:active{background-color:", ";border-color:", ";}"], colors[`${selectedColor}8`], colors[selectedColor], colors[selectedColor], colors[`${selectedColor}16`], colors[selectedColor]), getFocusRing(colors.purple56));
      }

    case 'tertiary':
      {
        const isDark = props.theme.type === 'dark';
        const color = isDark ? 'white' : intentColorMap[props.intent];
        const bg = isDark ? 'white' : 'black';

        if (shouldHaveDisabledStyle) {
          return css(["color:", ";"], isDark ? colors.whiteAlpha48 : colors.blackAlpha48);
        }

        return css(["color:", ";&:hover{background-color:", ";}&:active{background-color:", ";}&.focus-visible{box-shadow:", ";}"], colors[color], colors[`${bg}Alpha4`], colors[`${bg}Alpha16`], getFocusRing(colors.purple56));
      }

    default:
      {
        const {
          intent
        } = props;
        const color = intent === 'default' ? 'purple' : intentColorMap[intent];

        if (shouldHaveDisabledStyle) {
          return css(["color:", ";background-color:", ";"], colors.black48, colors.black8);
        }

        return css(["color:", ";background-color:", ";&:hover{background-color:", ";}&:active{background-color:", ";}&.focus-visible{box-shadow:", ";}"], colors.white, colors[color], colors[`${color}Darken8`], colors[`${color}Darken16`], getFocusRing(colors.purple56));
      }
  }
}

function getButtonSize(props) {
  let style = {};

  switch (props.size) {
    case 'xsmall':
      style = {
        height: rem(24),
        padding: `0 ${unit(2)}`
      };
      break;

    case 'small':
      style = {
        height: rem(32),
        padding: `0 ${unit(2)}`
      };
      break;

    case 'large':
      style = {
        height: rem(48),
        padding: `0 ${unit(4)}`
      };
      break;

    default:
      style = {
        height: rem(40),
        padding: `0 ${unit(3)}`
      };
      break;
  }

  if (props.icononly) {
    style.padding = 0;
    style.width = style.height;
  }

  if (props.stretch) {
    style.width = '100%';
  }

  style.lineHeight = `calc(${style.height} - 2px)`;
  return style;
}

const ButtonBase = /*#__PURE__*/styled.button.withConfig({
  displayName: "ButtonBase",
  componentId: "sc-1a3rtp-3"
})(["", " ", " position:relative;border-radius:4px;border:1px solid transparent;white-space:nowrap;", " ", " & > span + span{margin-left:", ";}"], resetButtonStyleWithTransition, buttonLabel, getButtonSize, getButtonVariant, unit(1));
const Button = /*#__PURE__*/forwardRef(function Button(_ref, forwardedRef) {
  let {
    as: controlledAs,
    type = 'button',
    href,
    busy = false,
    children,
    size = 'medium',
    variant = 'secondary',
    intent = 'default',
    disabled = false,
    glyph,
    glyphStart,
    glyphEnd,
    stretch = false,
    onClick,
    // eslint-disable-next-line react/prop-types
    isIconButton
  } = _ref,
      restProps = _objectWithoutPropertiesLoose(_ref, ["as", "type", "href", "busy", "children", "size", "variant", "intent", "disabled", "glyph", "glyphStart", "glyphEnd", "stretch", "onClick", "isIconButton"]);

  const isLink = href != null;
  const as = controlledAs || (isLink ? 'a' : undefined);
  const iconOnly = Boolean((glyph || glyphStart || glyphEnd) && !children);

  if (!children && !isIconButton) {
    warnOnce('Button is being rendered without children. Consider using IconButton instead.');
  }

  if (isLink) {
    // eslint-disable-next-line no-param-reassign
    type = undefined;
  }

  if (glyph) {
    // eslint-disable-next-line no-param-reassign
    glyphStart = glyph;
  }

  const handleClick = event => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  return /*#__PURE__*/_jsxs(ButtonBase, Object.assign({
    ref: forwardedRef,
    as: as
  }, restProps, {
    type: type,
    href: href,
    size: size,
    intent: intent,
    variant: variant,
    disabled: !isLink && (disabled || busy),
    "aria-disabled": isLink ? disabled || undefined : undefined,
    "aria-busy": busy || undefined,
    icononly: iconOnly || undefined,
    stretch: stretch || undefined,
    onClick: handleClick,
    children: [busy && /*#__PURE__*/_jsx(ButtonLoader, {
      numDots: iconOnly ? 1 : 3,
      size: 2.5
    }), glyphStart && /*#__PURE__*/_jsx(ButtonIcon, {
      glyph: glyphStart,
      size: iconOnly ? size : 'xsmall'
    }), children && /*#__PURE__*/_jsx(ButtonLabel, {
      "aria-hidden": busy || undefined,
      children: children
    }), glyphEnd && /*#__PURE__*/_jsx(ButtonIcon, {
      glyph: glyphEnd,
      size: "xsmall"
    })]
  }));
});
Button.propTypes = {
  as: T.elementType,
  href: T.string,
  size: T.oneOf(['large', 'medium', 'small', 'xsmall']),
  type: T.oneOf(['button', 'submit', 'reset']),
  variant: T.oneOf(['primary', 'secondary', 'tertiary']),
  intent: T.oneOf(['default', 'primary', 'danger', 'success', 'warning']),
  disabled: T.bool,
  glyph: T.elementType,
  glyphStart: T.elementType,
  glyphEnd: T.elementType,
  children: T.node,
  busy: T.bool,
  stretch: T.bool,
  onClick: T.func
};
export { Button };